"use client";

import { DetailView, costLabel } from "./battleDemoTypes";
import { JUDGMENT_LABEL } from "./characterViews";

/** 作用の色。何が効くか は属性の色、当たり方 は鋼の色、その他 は紫の系統で塗る */
const ASPECT_COLOR: Record<string, string> = {
  熱: "#f08a4b", 寒: "#7fd0f0", 水: "#5b9cf0", 電: "#f0dc55", 聖: "#f7ecb0", 闇: "#9a7fe0", 爆: "#f0664b", 磁: "#c58ad8", 塵: "#c9b48a",
  斬: "#c3cad6", 打: "#c3cad6", 刺: "#c3cad6", 弾: "#c3cad6", 圧: "#c3cad6", 縛: "#c3cad6", 裂: "#c3cad6", 捻: "#c3cad6", 震: "#c3cad6",
  腐: "#9ab06a", 毒: "#93d05a", 呪: "#c070c8", 乾: "#d8b070", 幻: "#e0a0d8", 眠: "#a0b0e8", 音: "#80d8c8", 息: "#b8e0d0", 吸: "#d06a8a",
  魅: "#f08ab8", 恐: "#8a88a8", 病: "#a8b860", 死: "#8a8a96", 変: "#b890e0",
};

/** 作用の印。1 字を色つきの札で出す */
export function Asp({ label }: { label: string }) {
  return <span className="bd-asp" style={{ ["--bd-asp" as string]: ASPECT_COLOR[label] ?? "#b9c0cc" }}>{label}</span>;
}

/** 下からせり上がるモーダル。locked のときは背景と × で閉じない */
export function Modal({ children, onClose, locked }: { children: React.ReactNode; onClose: () => void; locked?: boolean }) {
  return (
    <div className="bd-modal">
      <div className="bd-modal-back" onClick={locked ? undefined : onClose} />
      <div className="bd-modal-body">
        {!locked && <button className="bd-modal-x" onClick={onClose} aria-label="閉じる">×</button>}
        {children}
      </div>
    </div>
  );
}

function Kv({ values, labels, format, tone }: {
  values: Record<string, number>;
  labels: Record<string, string>;
  format?: (v: number) => string;
  tone?: (v: number) => string;
}) {
  return (
    <div className="bd-kv">
      {Object.keys(labels).filter((k) => values[k] !== undefined).map((k) => (
        <div key={k} className={tone ? tone(values[k]) : ""}>
          <span>{labels[k]}</span>
          <b>{format ? format(values[k]) : String(values[k])}</b>
        </div>
      ))}
    </div>
  );
}

const DAMAGE_TAKEN_LABEL = { physical: "物理", magic: "魔法", all: "全部", critical: "会心" };

function turnsText(t: number | string | null): string {
  if (typeof t === "number") return `残り ${t} ターン`;
  if (t === "battle") return "戦闘の間";
  return "続く";
}

/** 人物の詳細。能力・状態・装備・スキル・持ち物・耐性を並べる */
export function DetailBody({ detail }: { detail: DetailView }) {
  const d = detail;
  const resShow = (x: number) => (Math.round((2 - x) * 10) / 10).toFixed(1);
  return (
    <div>
      <div className="bd-m-title">{d.name}</div>
      <div className="bd-m-sub">{d.sub}</div>
      <div className="bd-kv">
        <div><span>HP</span><b>{d.hp.current}/{d.hp.max}</b></div>
        {d.pools.map((p) => <div key={p.type}><span>{p.type.toUpperCase()}</span><b>{p.current}/{p.max}</b></div>)}
        {d.shield > 0 && <div><span>身代わり</span><b>{d.shield}</b></div>}
        {d.money > 0 && <div><span>CC</span><b>{d.money}</b></div>}
      </div>

      <div className="bd-m-sec">
        <h3>スキル</h3>
        <div className="bd-m-list">
          {d.skills.map((s, i) => (
            <div key={i} className="bd-m-item">
              <div className="bd-mi1">
                <span><b>{s.name}</b>{s.aspects.map((a) => <Asp key={a} label={a} />)}</span>
                <span className="bd-note">
                  {[
                    s.kind,
                    s.cost.map(costLabel).join(" "),
                    s.cooldown ? `待ち ${s.cooldown}` : "",
                    s.uses !== null && s.uses !== undefined ? `${s.uses} 回` : "",
                    s.uses_left !== null ? `残り ${s.uses_left}` : "",
                    s.cooldown_left ? `あと ${s.cooldown_left}` : "",
                    s.granted ? "装備の付与" : "",
                  ].filter(Boolean).join("・")}
                </span>
              </div>
              <div className="bd-mi2">{s.description}</div>
            </div>
          ))}
          {d.skills.length === 0 && <div className="bd-note">なし</div>}
        </div>
      </div>

      <div className="bd-m-sec">
        <h3>装備</h3>
        <div className="bd-m-list">
          {d.parts.filter((p) => p.items.length > 0).map((p) => (
            <div key={p.name} className="bd-m-item">
              {p.items.map((it, i) => (
                <div key={i}>
                  <div className="bd-mi1">
                    <span><span className="bd-note">{p.name}　</span><b>{it.name}</b></span>
                    <span className="bd-note">
                      {[it.durability ? `耐久 ${it.durability.current}/${it.durability.max}` : "", it.pool ? `${it.pool.type.toUpperCase()} ${it.pool.current}/${it.pool.max}` : "", p.blocked ? "部位に状態" : ""].filter(Boolean).join("・")}
                    </span>
                  </div>
                  <div className="bd-mi2">{it.summary}</div>
                </div>
              ))}
            </div>
          ))}
          {d.parts.every((p) => p.items.length === 0) && <div className="bd-note">なし</div>}
        </div>
      </div>

      <div className="bd-m-sec">
        <h3>状態</h3>
        {d.modifiers.length === 0 && <div className="bd-note">なし</div>}
        <div className="bd-m-list">
          {d.modifiers.map((m, i) => (
            <div key={i} className="bd-m-item">
              <div className="bd-mi1">
                <b style={{ color: m.kind === "buff" ? "#8fe3b5" : "#f5a3c8" }}>{m.label}{m.stacks > 1 ? ` ×${m.stacks}` : ""}</b>
                <span className="bd-note">{turnsText(m.turns_left)}</span>
              </div>
              <div className="bd-mi2">{m.summary}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="bd-m-sec">
        <h3>持ち物</h3>
        {d.items.length === 0 && <div className="bd-note">なし</div>}
        <div className="bd-m-list">
          {d.items.map((it, i) => (
            <div key={i} className="bd-m-item">
              <div className="bd-mi1"><b>{it.name}</b><span>×{it.count}</span></div>
              <div className="bd-mi2">{it.description}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="bd-m-sec"><h3>基礎実数値</h3><Kv values={d.base} labels={d.labels.base} /></div>
      <div className="bd-m-sec"><h3>判定実数値</h3><Kv values={d.judgment} labels={JUDGMENT_LABEL} /></div>
      <div className="bd-m-sec"><h3>被弾実数値</h3><Kv values={d.damage_taken} labels={DAMAGE_TAKEN_LABEL} format={(x) => x.toFixed(2)} /></div>
      <div className="bd-m-sec">
        <h3>耐性</h3>
        <Kv values={d.resistance} labels={d.labels.resistance} format={resShow} tone={(x) => (x < 1 ? "bd-good" : x > 1 ? "bd-bad" : "")} />
      </div>
    </div>
  );
}
